/* eslint-disable no-redeclare */
import { z } from 'zod';

const ISharedSessionTrafficContextPayloadApiDTO = z.object({
  Provider: z.string().nullable().optional(),
  Keys: z
    .object({
      Name: z.string().nullable(),
      Value: z.string().nullable(),
    })
    .array()
    .nullable(),
});

type ISharedSessionTrafficContextPayloadApiDTO = z.infer<
  typeof ISharedSessionTrafficContextPayloadApiDTO
>;

const ISharedSessionFlowContextPayloadApiDTO = z.object({
  FlowId: z
    .string()
    .nullable()
    .optional()
    .default('')
    .describe(
      "The ID of the flow that it's been tested. When several flows are tested for the experiment",
    ),
  ExperimentId: z
    .string()
    .default('')
    .nullable()
    .optional()
    .describe('The ID of the experiment (usually from Optimizely)'),
  FlowVersion: z
    .string()
    .default('')
    .nullable()
    .optional()
    .describe('The version of the flow that is being tested'),
  ExperimentVariation: z
    .string()
    .default('')
    .nullable()
    .optional()
    .describe('The experiment variation code'),
  FlowStep: z
    .string()
    .default('')
    .nullable()
    .optional()
    .describe('The step in the flow where the experiment takes place'),
});

/**
 * The flow context is a block of the session to track experiments at session level.
 * This block is assigned when the session is created but it can also be updated later in the flow.
 * The purpose of this block is to track the monetization generated by each experimental group.
 *
 * @deprecated It seems this was the previous tool used to track experiments. Is it used anymore or can we remove it?
 */
type ISharedSessionFlowContextPayloadApiDTO = z.infer<
  typeof ISharedSessionFlowContextPayloadApiDTO
>;

const ISharedSessionLabelPayloadApiDTO = z.object({
  Channel: z
    .enum(['SEM', 'Email', 'Organic', 'ORGANIC'])
    .default('SEM')
    .nullable()
    .optional(),
  Domain: z.string(),
  Product: z
    .enum(['HEALTH', 'MEDICARE', 'MEDICARE GUIDE'])
    .nullable()
    .optional(),
  Src: z.string().default('').nullable().optional(),
  Source: z.string().default('').nullable().optional(),
  SubId: z.string().default('').nullable().optional(),
  Medium: z.string().default('').nullable().optional(),
  Keyword: z.string().default('').nullable().optional(),
  KeywordId: z.string().nullable().optional(),
  Campaign: z.string().default('').nullable().optional(),
  CampaignId: z.string().default('').nullable().optional(),
  AdGroup: z.string().default('').nullable().optional(),
  AdGroupId: z.string().default('').nullable().optional(),
  AdId: z.string().default('').nullable().optional(),
  LandingPage: z.string(),
  MatchType: z.string().default('').nullable().optional(),
  ImpactRadiusCampaignId: z.string().default('').nullable().optional(),
  OptimizelyExperimentId: z.string().default('').nullable().optional(),
  PublisherCampaignId: z.string().default('').nullable().optional(),
  Ircid: z.string().default('').nullable().optional(),
  Gclid: z.string().default('').nullable().optional(),
  agent_id: z.string().default('2010000').nullable().optional(),
  UtmRedirect: z.string().nullable().optional(),
  RedirectionDomain: z.string().nullable().optional(),
  Network: z
    .string()
    .nullable()
    .optional()
    .describe(
      'This is the display network configured in the traffic acquisition platform. This value is extracted from the bidding tool context',
    ),
  Placement: z
    .string()
    .nullable()
    .optional()
    .describe(
      'These are locations in the traffic acquisition display networks where the ads are shown.',
    ),
  Target: z
    .string()
    .nullable()
    .optional()
    .describe('The target configured in the traffic acquisition platform.'),
  FeedItemId: z
    .string()
    .nullable()
    .optional()
    .describe('Feed ID in the traffic acquisition platform.'),
  AdPosition: z
    .string()
    .nullable()
    .optional()
    .describe(
      'The Ad position in the traffic acquisition search engine. This value is extracted from the bidding tool context. This value is only configured for Google traffic.',
    ),
  BiddingToolParameterStatus: z
    .string()
    .nullable()
    .optional()
    .describe(
      'It indicates if there was an error while decoding the bidding tool parameter. If there is an error this field is set to 1.',
    ),
  BiddingToolParameterError: z
    .string()
    .nullable()
    .optional()
    .describe(
      'If there was an error while decoding the bidding tool parameter, it indicates the error, specifically the tech reason for the error.',
    ),
  AudienceId: z.string().nullable().optional(),
  LocationId: z.string().nullable().optional(),
  BiddingTool_Leadid: z.string().nullable().optional(),
  BiddingTool_Device: z.string().nullable().optional(),
  QueryTxt: z.string().nullable().optional(),
  BiddingTool_TrafficSourceId: z.string().nullable().optional(),
  BiddingTool_version: z.string().nullable().optional(),
});

/**
 * This block identifies how we acquire the user.
 */
type ISharedSessionLabelPayloadApiDTO = z.infer<
  typeof ISharedSessionLabelPayloadApiDTO
>;

const ISharedSessionUserContextPayloadApiDTO = z.object({
  UserId: z
    .string()
    .nullable()
    .optional()
    .describe(
      'It is a unique identifier for the user.  It is assigned when the user lands in any of the flows for the first time.',
    ),
  ClientId: z
    .string()
    .default('')
    .nullable()
    .optional()
    .describe(
      "DEPRECATED: It's an intermediate value between the session and the user id, it allows us to identify the computer and browser the user is using.",
    ),
  SessionId: z
    .string()
    .default('')
    .nullable()
    .optional()
    .describe('It is a unique identifier of the session.'),
  UserAgent: z
    .string()
    .optional()
    .describe(
      'It is a value that identifies the browser, operating system and computer the user is using to navigate on the web.',
    ),
  IpAddress: z
    .string()
    .default('')
    .nullable()
    .optional()
    .describe("User's public IP address."),
  Browser: z
    .string()
    .nullable()
    .optional()
    .describe(
      'Browser that the user is using, this value is extracted from user agent.',
    ),
  BrowserVersion: z
    .string()
    .nullable()
    .optional()
    .describe('Version of browser that the user is using'),
  OperatingSystem: z
    .string()
    .default('')
    .nullable()
    .optional()
    .describe("User's Operating system"),
  OperatingSystemVersion: z
    .string()
    .nullable()
    .optional()
    .describe("The version of the user's operating system"),
  Device: z
    .string()
    .default('')
    .nullable()
    .optional()
    .describe(
      'The device that the user is using, this value is extracted from the user agent',
    ),
  LoggedInAgentId: z.string().nullable().optional().default(''),

  Location: z.string().nullable().optional(),
  Network: z.string().nullable().optional(),
  IsNewUser: z
    .number()
    .nullable()
    .optional()
    .describe(
      'DEPRECATED: This value is intended to determine if the user is a returning user or not.',
    ),
  ParentSessionId: z
    .string()
    .nullable()
    .optional()
    .describe('Id of the current session if the intention is to reuse it.'),
});

/**
 * The user context allows us to identify the user.
 */
type ISharedSessionUserContextPayloadApiDTO = z.infer<
  typeof ISharedSessionUserContextPayloadApiDTO
>;

export const PostSessionPayloadDTO = z.object({
  Email: z.string().nullable().default(null).nullable().optional(),
  TrafficSource: z
    .string()
    .default('')
    .nullable()
    .optional()
    .describe(
      'Examples can be Google, Bing, Yahoo. If traffic is paid this field should be picked from utm_source parameter or if it is a search engine the referrer url should parsed; otherwise DIRECT traffic is assumed',
    ),
  BiddingToolId: z.string().default('').nullable().optional(),
  BiddingToolName: z.string().nullable().optional(),
  Referrer: z.string().nullable().optional(),
  Label: ISharedSessionLabelPayloadApiDTO.optional(),
  UserContext: ISharedSessionUserContextPayloadApiDTO,
  FlowContext: ISharedSessionFlowContextPayloadApiDTO.optional(),
  TrafficContext:
    ISharedSessionTrafficContextPayloadApiDTO.nullable().optional(),
});

export const UpdateSessionPayloadDTO = z.object({
  TrafficContext:
    ISharedSessionTrafficContextPayloadApiDTO.nullable().optional(),
});

/**
 * Session structure as described in the [TRON project documentation](https://docs.google.com/document/d/1AkR8xk-RzLV27PhTIpCa7zuOczM-TIkPD1eca7xUI8k/edit#heading=h.p3h2dscpr8fp)
 */
export type PostSessionPayloadDTO = z.infer<typeof PostSessionPayloadDTO>;
export type UpdateSessionPayloadDTO = z.infer<typeof UpdateSessionPayloadDTO>;
